import { isEmpty } from "lodash";
import { DragEvent, MouseEvent, memo, useCallback, useContext, useRef, useState } from "react";
import ReactFlow, {
  Controls,
  Edge,
  MarkerType,
  Node,
  addEdge,
  getConnectedEdges,
  useReactFlow,
  useStoreApi,
  useUpdateNodeInternals
} from "reactflow";
import "reactflow/dist/style.css";
import { Message } from "semantic-ui-react";
import { v4 as uuidv4 } from "uuid";
import { proOptionsForReactFlow } from "../../config/constants";
import { nodeCategories, nodeType } from "../../config/drawingConstants";
import { ArrowHeadSvg, ArrowHeadSvgSelected } from "./components/ArrowHeadSvg";
import CustomConnectionLine from "./components/CustomConnectionLine";
import { DrawingContext } from "./DrawingTool";
import "./DrawingTool.scss";
import Cursors from "./drawingToolItems/Cursors";
import CustomNode from "./drawingToolItems/CustomNode";
import FloatingEdge from "./drawingToolItems/FloatingEdge";
import GroupNode from "./drawingToolItems/GroupNode";
import IngredientGroupNode from "./drawingToolItems/IngredientGroupNode";
import useCursorStateSynced from "./hooks/useCursorStateSynced";
import {
  checkEdgeValidation,
  getNodePositionInsideParent,
  nodeValidationChecker,
  sortNodes
} from "./utils";

const nodeTypes = {
  special: CustomNode,
  farmGate: memo(GroupNode),
  processGate: memo(GroupNode),
  ingredientGate: IngredientGroupNode
};

const edgesTypes = {
  special: FloatingEdge
};

//create process gate
const defaultEdgeOptions = {
  style: { strokeWidth: 3, stroke: "var(--mappingArrow)" },
  type: "special",
  markerEnd: {
    strokeWidth: 2,
    width: 20,
    height: 20,
    type: MarkerType.Arrow,
    color: "var(--mappingArrow)"
  }
};

const connectionLineStyle = {
  strokeWidth: 2,
  stroke: "var(--mappingArrow)"
};

const rfStyle = {
  backgroundColor: "#FFF"
};

const CanvasMain = () => {
  // State Variable
  const {
    chartNodes,
    chartEdges,
    lockStatus,
    setChartNodes,
    setChartEdges,
    onEdgesChange,
    onNodesChange,
    updateChartGroup,
    saveDrawing,
    toggleProcessGateModel
  } = useContext(DrawingContext);

  const reactFlowWrapper = useRef<any>(null);
  const [reactFlowInstance, setReactFlowInstance] = useState<any>(null);
  const [validationMessages, setValidationMessages] = useState<any[]>([]);
  const [deleteKeyCode, setDeleteKeyCode] = useState<any>("Backspace");
  const updateNodeInternals = useUpdateNodeInternals();
  const [cursors, onMouseMove] = useCursorStateSynced();
  const { getIntersectingNodes } = useReactFlow();
  const store = useStoreApi();

  const processedNodes = chartNodes.map((node: Node) => ({
    ...node,
    draggable: !lockStatus,
    selectable: !lockStatus
  }));

  const processedEdges = chartEdges.map((edge: Edge) => ({
    ...edge,
    selectable: !lockStatus
  }));

  const validationForEdges = useCallback(
    (connection: any) => {
      const { isValid, validationMessage } = checkEdgeValidation(
        connection,
        chartEdges,
        chartNodes,
        lockStatus
      );
      if (!isValid) {
        setValidationMessages([...validationMessages, ...validationMessage]);
      }
      return isValid;
    },
    [setValidationMessages, validationMessages, chartNodes, chartEdges, lockStatus]
  );

  const onConnect = useCallback(
    (connection: any) => {
      const isValid = validationForEdges(connection);
      if (isValid) {
        const newEdge = {
          ...connection,
          type: "special",
          data: { endLabel: "test" }
        };
        setChartEdges(addEdge(newEdge, chartEdges));
      }
    },
    [chartEdges, chartNodes, validationForEdges]
  );

  // Add a farmGate to drawing
  const addFarmGate = useCallback(
    (farmGateNode: Node) => {
      const newNode: Node = {
        id: uuidv4(),
        type: nodeCategories.special,
        data: {
          label: "Cultivation",
          icon: nodeType.Cultivar,
          description: "",
          reference: null
        },
        parentNode: farmGateNode.id,
        extent: "parent",
        position: { x: 40, y: 40 },
        style: undefined
      };
      setChartNodes([...chartNodes, farmGateNode, newNode]);
      updateNodeInternals(farmGateNode.id);
      updateNodeInternals(newNode.id);
    },
    [chartNodes, updateNodeInternals, setChartNodes]
  );

  //save new node
  const saveNewNodeData = useCallback(
    (newNode: any) => {
      if (newNode.type === nodeCategories.processGate) {
        toggleProcessGateModel(newNode, false);
        return;
      }
      if (newNode.type === nodeCategories.farmGate) {
        addFarmGate(newNode);
        return;
      }
      setChartNodes([...chartNodes, newNode]);
      updateNodeInternals(newNode.id);
    },
    [chartNodes, updateNodeInternals, addFarmGate]
  );

  const onDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const addNewNodeInsideGroup = useCallback((position: any) => {
    const intersections = getIntersectingNodes({
      x: position.x,
      y: position.y,
      width: 100,
      height: 40
    }).filter(
      (n: Node) => n.type === nodeCategories.farmGate || n.type === nodeCategories.processGate
    );

    const groupNode = intersections[intersections.length - 1];
    if (groupNode) {
      // if we drop a node on a group node, we want to position the node inside the group
      const positionInGroup = getNodePositionInsideParent(
        {
          position,
          width: 100,
          height: 60
        },
        groupNode
      ) ?? { x: 0, y: 0 };
      const parentNode = groupNode?.id;
      return {
        isInGroup: true,
        positionInGroup,
        parentNode,
        groupNode
      };
    }
    return {
      isInGroup: false,
      positionInGroup: { x: 0, y: 0 },
      parentNode: undefined,
      groupNode: undefined
    };
  }, []);

  const validationForNodes = useCallback(
    (node: Node, group: Node | undefined) => {
      const { isValid, validationMessage } = nodeValidationChecker(
        node,
        group,
        chartNodes,
        lockStatus
      );
      if (!isValid) {
        setValidationMessages([...validationMessages, validationMessage]);
      }
      return isValid;
    },
    [setValidationMessages, validationMessages, chartNodes, chartEdges, lockStatus]
  );

  const addNewNode = useCallback(
    (position: any, node: any) => {
      let nodeType = nodeCategories.special;
      switch (node.id) {
        case nodeCategories.farmGate:
          nodeType = nodeCategories.farmGate;
          break;
        case nodeCategories.processGate:
          nodeType = nodeCategories.processGate;
          break;
      }
      let nodeStyle = nodeType !== nodeCategories.special ? { width: 300, height: 250 } : undefined;
      const newNode: Node = {
        id: uuidv4(),
        type: nodeType,
        data: {
          label: node.cardHeader,
          icon: node.id,
          description: "",
          reference: null
        },
        position,
        style: nodeStyle
      };
      const { isInGroup, positionInGroup, parentNode, groupNode } = addNewNodeInsideGroup(position);
      if (isInGroup) {
        newNode.position = positionInGroup;
        newNode.parentNode = parentNode;
        newNode.extent = parentNode ? "parent" : undefined;
      }
      if (groupNode && groupNode?.type === nodeCategories.ingredientGate) {
        return;
      }
      const isValid = validationForNodes(newNode, groupNode);
      if (!isValid) return;

      saveNewNodeData(newNode);
    },
    [reactFlowInstance, chartNodes, addNewNodeInsideGroup, validationForNodes, saveNewNodeData]
  );

  // delete edges and save drawing after deleted nodes
  const onNodesDelete = useCallback(
    (deletedNodes: Node[]) => {
      if (lockStatus) return;

      const remainingEdges = deletedNodes.reduce((acc, node) => {
        const connectedEdges = getConnectedEdges([node], chartEdges);
        const remainingEdge = acc.filter((edge: Edge) => !connectedEdges.includes(edge));
        return [...remainingEdge];
      }, chartEdges);
      const remainingNodes = chartNodes.filter(
        (cn: Node) => !deletedNodes.some((dn: Node) => dn.id === cn.id || dn.id === cn.parentNode)
      );
      saveDrawing(remainingEdges, remainingNodes);
    },
    [chartNodes, chartEdges, lockStatus, saveDrawing]
  );

  // add new node in to canvas
  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      const nodeType = event.dataTransfer.getData("application/reactflow");

      // check if the dropped element is valid
      if (typeof nodeType === "undefined" || !nodeType) {
        return;
      }
      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top
      });

      addNewNode(position, JSON.parse(nodeType));
    },
    [reactFlowInstance, chartNodes, addNewNode]
  );

  // Helper: Move ingredients outside when a gate is dragged over them
  const moveIngredientsOutOfGate = useCallback(
    (gateNode: Node): boolean => {
      const allNodes = store.getState().getNodes();
      const ingredientNodes = allNodes.filter(
        (n: Node) => n.data.icon === nodeType.Ingredients && !n.parentNode
      );

      const ingredientsInGate = ingredientNodes.filter((ingredientNode: Node) => {
        const intersectingGates = getIntersectingNodes(ingredientNode).filter(
          (g: Node) => g.id === gateNode.id
        );
        return intersectingGates.length > 0;
      });

      if (ingredientsInGate.length > 0) {
        const nextNodes: Node[] = allNodes
          .map((n: Node) => {
            const ingredientToMove = ingredientsInGate.find((ing: Node) => ing.id === n.id);
            if (ingredientToMove) {
              return {
                ...n,
                position: {
                  x: gateNode.position.x + (gateNode.width || 300) + 50,
                  y: gateNode.position.y + ingredientsInGate.indexOf(ingredientToMove) * 80
                },
                parentNode: undefined,
                extent: undefined
              };
            }
            return n;
          })
          .sort(sortNodes);
        setChartNodes(nextNodes);
        return true;
      }
      return false;
    },
    [getIntersectingNodes, setChartNodes, store]
  );

  // Helper: Move ingredient outside when dragged into a gate
  const moveIngredientOutOfIntersectingGate = useCallback(
    (ingredientNode: Node, gateNode: Node): void => {
      const nextNodes: Node[] = store
        .getState()
        .getNodes()
        .map((n: Node) => {
          if (n.id === gateNode.id) {
            return {
              ...n,
              className: ""
            };
          } else if (n.id === ingredientNode.id) {
            return {
              ...n,
              position: {
                x: gateNode.position.x + (gateNode.width || 300) + 50,
                y: gateNode.position.y
              },
              parentNode: undefined,
              extent: undefined,
              dragging: false
            };
          }
          return n;
        })
        .sort(sortNodes);
      setChartNodes(nextNodes);
    },
    [setChartNodes, store]
  );

  // Helper: Remove ingredient from gate parent relationship
  const detachIngredientFromGateParent = useCallback(
    (ingredientNode: Node, parentGateNode: Node): void => {
      const nextNodes: Node[] = store
        .getState()
        .getNodes()
        .map((n: Node) => {
          if (n.id === ingredientNode.id) {
            return {
              ...n,
              position: {
                x: (parentGateNode.position.x || 0) + (n.position?.x || 0),
                y: (parentGateNode.position.y || 0) + (n.position?.y || 0)
              },
              parentNode: undefined,
              extent: undefined,
              dragging: false
            };
          }
          return n;
        })
        .sort(sortNodes);
      setChartNodes(nextNodes);
    },
    [setChartNodes, store]
  );

  // Helper: Attach node to parent group
  const attachNodeToParentGroup = useCallback(
    (node: Node, groupNode: Node): void => {
      const nextNodes: Node[] = store
        .getState()
        .getNodes()
        .map((n: Node) => {
          if (n.id === groupNode.id) {
            return {
              ...n,
              className: ""
            };
          } else if (n.id === node.id) {
            const position = getNodePositionInsideParent(n, groupNode) ?? {
              x: 0,
              y: 0
            };
            return {
              ...n,
              position,
              parentNode: groupNode.id,
              extent: "parent" as const,
              dragging: false
            };
          }
          return n;
        })
        .sort(sortNodes);
      setChartNodes(nextNodes);
    },
    [setChartNodes, store]
  );

  const onNodeDragStop = useCallback(
    (_: MouseEvent, node: Node) => {
      if (
        (node.type === nodeCategories.farmGate || node.type === nodeCategories.processGate) &&
        !node.parentNode
      ) {
        const wasHandled = moveIngredientsOutOfGate(node);
        if (wasHandled) return;
      }
      if (node.type !== nodeCategories.special && !node.parentNode) {
        return;
      }
      const intersections = getIntersectingNodes(node).filter(
        (n: Node) => n.type !== nodeCategories.special
      );
      const groupNode = intersections[intersections.length - 1];

      if (groupNode && groupNode?.type === nodeCategories.ingredientGate) {
        return;
      }

      if (
        node.data.icon === nodeType.Ingredients &&
        groupNode &&
        (groupNode.type === nodeCategories.farmGate ||
          groupNode.type === nodeCategories.processGate)
      ) {
        moveIngredientOutOfIntersectingGate(node, groupNode);
        return;
      }

      if (node.data.icon === nodeType.Ingredients && node.parentNode) {
        const parentNode = store
          .getState()
          .getNodes()
          .find((n: Node) => n.id === node.parentNode);

        if (
          parentNode &&
          (parentNode.type === nodeCategories.farmGate ||
            parentNode.type === nodeCategories.processGate)
        ) {
          detachIngredientFromGateParent(node, parentNode);
          return;
        }
      }

      const isValid = validationForNodes(node, groupNode);
      if (!isValid) return;

      if (intersections.length && node.parentNode !== groupNode?.id) {
        attachNodeToParentGroup(node, groupNode);
      }
    },
    [
      getIntersectingNodes,
      store,
      validationForNodes,
      moveIngredientsOutOfGate,
      moveIngredientOutOfIntersectingGate,
      detachIngredientFromGateParent,
      attachNodeToParentGroup
    ]
  );

  const onNodeDrag = useCallback(
    (_: MouseEvent, node: Node) => {
      if (node.type !== nodeCategories.special && !node.parentNode) {
        return;
      }
      const intersections = getIntersectingNodes(node).filter(
        (n: Node) => n.type !== nodeCategories.special
      );
      const groupClassName =
        intersections.length && node.parentNode !== intersections[0]?.id ? "active" : "";
      updateChartGroup(groupClassName, node);
    },
    [getIntersectingNodes, updateChartGroup]
  );

  const onNodeClick = useCallback(
    (e: any, node: Node) => {
      if (!isEmpty(node.data.reference) || node.data.icon === nodeType.Cultivar) {
        setDeleteKeyCode(null);
        return;
      }
      setDeleteKeyCode("Backspace");
    },
    [setDeleteKeyCode]
  );

  const onEdgeClick = useCallback(
    (e: any, edge: Edge) => {
      setDeleteKeyCode("Backspace");
    },
    [setDeleteKeyCode]
  );

  return (
    <div className="drawing-wrapper" ref={reactFlowWrapper}>
      {validationMessages.length > 0 ? (
        <div className="validation-box">
          <Message
            color="red"
            onDismiss={() => {
              setValidationMessages([]);
            }}
          >
            <Message.Header>These are not accepted!</Message.Header>
            <Message.List>
              {validationMessages.map((message: string) => (
                <Message.Item>{message}</Message.Item>
              ))}
            </Message.List>
          </Message>
        </div>
      ) : null}
      <ArrowHeadSvg />
      <ArrowHeadSvgSelected />
      <ReactFlow
        nodes={processedNodes}
        onNodesChange={onNodesChange}
        proOptions={proOptionsForReactFlow}
        edges={processedEdges}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        deleteKeyCode={deleteKeyCode}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeDrag={onNodeDrag}
        onNodeDragStop={onNodeDragStop}
        style={rfStyle}
        nodeTypes={nodeTypes}
        edgeTypes={edgesTypes}
        onNodesDelete={onNodesDelete}
        onInit={setReactFlowInstance}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onPointerMove={onMouseMove}
        selectNodesOnDrag={false}
        defaultEdgeOptions={defaultEdgeOptions}
        connectionLineComponent={CustomConnectionLine}
        connectionLineStyle={connectionLineStyle}
      >
        <Cursors cursors={cursors} />
        <Controls />
      </ReactFlow>
    </div>
  );
};

export default CanvasMain;
